import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SHOWROOM_PATH_IDENTIFIER } from "@faire/retailer-shared/categories/pathIdentifier";
import { optimizeProductImageUrlFillVertical } from "@faire/web--source/common/images/optimizeImages";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { EmbeddedLink } from "@faire/web--source/ui/EmbeddedLink";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { TransparencyOverlay } from "@faire/web--source/ui/TransparencyOverlay";
import { ILayoutElement } from "@faire/web-api--source/indigofair/data/ILayoutElement";
import { path as categoryShowroom } from "@faire/web-api--source/routes/category/Showroom";
import React from "react";
import styled, { css } from "styled-components";
import { AnimatedCategoryTile } from "@faire/retailer/components/Carousel/AnimatedCategoryTile";
import { ImageTextLinkTile, ImageTextLinkTileStyles } from "@faire/retailer/components/Carousel/ImageTextLinkTile";
import { AspectRatioPicture } from "@faire/retailer/components/Image/AspectRatioPicture";
import { CarouselBase } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Carousel/CarouselBase";
import { Context } from "@faire/retailer/components/LayoutElementRenderer/__internal__/Context";
import { EAEAEA_PIXEL } from "@faire/retailer/consts/EAEAEA_PIXEL";
import { ShowroomAnimatedCategoryTile } from "@faire/retailer/features/Showroom/ShowroomAnimatedCategoryTile/ShowroomAnimatedCategoryTile";
import { VERTICAL_ASPECT_RATIO } from "@faire/retailer/lib/verticalAspectRatio";
var isSimpleImageTile = function(tile) {
    return "main_text" in (tile !== null && tile !== void 0 ? tile : {});
};
var isCategoryTile = function(tile) {
    return "category_path_identifier" in (tile !== null && tile !== void 0 ? tile : {});
};
var ITEMS_PER_SLIDE_HOMEPAGE = {
    mobileAndAbove: 2,
    tabletAndAbove: 4,
    desktopAndAbove: 6
};
export var TallCategoryTiles = function(props) {
    var CarouselBaseComponent = CarouselBase;
    var _React_useContext = React.useContext(Context), layoutElement = _React_useContext.layoutElement, onItemClick = _React_useContext.onItemClick, requestId = _React_useContext.requestId, onVisible = _React_useContext.onVisible;
    var isSSR = useIsSSR();
    var getItems = function() {
        return layoutElement.layout_element_data.map(function(elementData) {
            var _elementData_category_tile;
            return (_elementData_category_tile = elementData.category_tile) !== null && _elementData_category_tile !== void 0 ? _elementData_category_tile : elementData.simple_image_title;
        }).filter(isNotUndefined).filter(function(tile) {
            return isNotUndefined(isCategoryTile(tile) ? tile.name : tile.main_text);
        }).filter(function(tile) {
            return isNotUndefined(tile.image) || isNotUndefined(tile.images) && tile.images.length > 0;
        });
    };
    var expectedItemsPerSlideCategoryPage = useResponsiveValue({
        mobileAndAbove: 2,
        tabletAndAbove: 4,
        desktopAndAbove: 6,
        xLargeAndAbove: 7
    });
    var actualItemsPerSlideCategoryPage = Math.min(getItems().length, expectedItemsPerSlideCategoryPage);
    var imageSizeInfo = function() {
        if (layoutElement.display_page !== ILayoutElement.DisplayPage.CATEGORY_PAGE && layoutElement.display_page !== ILayoutElement.DisplayPage.SHOWROOM_PAGE) {
            return {
                height: 250,
                width: 250 * VERTICAL_ASPECT_RATIO
            };
        }
        if (expectedItemsPerSlideCategoryPage - actualItemsPerSlideCategoryPage === 1) {
            return {
                height: 246,
                width: 246
            };
        }
        if (expectedItemsPerSlideCategoryPage - actualItemsPerSlideCategoryPage === 2) {
            return {
                height: 219,
                width: 329
            };
        }
        if (expectedItemsPerSlideCategoryPage - actualItemsPerSlideCategoryPage === 3) {
            return {
                height: 304,
                width: 456
            };
        }
        if (expectedItemsPerSlideCategoryPage - actualItemsPerSlideCategoryPage === 4) {
            return {
                height: 405,
                width: 608
            };
        }
        return {
            height: 256,
            width: 192
        };
    };
    var renderSimpleImageTitleTile = function(tile, index) {
        if (layoutElement.display_page !== ILayoutElement.DisplayPage.CATEGORY_PAGE) {
            return null;
        }
        var image = tile.image, _tile_images = tile.images, images = _tile_images === void 0 ? [] : _tile_images, _tile_cta_link = tile.cta_link, cta_link = _tile_cta_link === void 0 ? "" : _tile_cta_link, _tile_main_text = tile.main_text, main_text = _tile_main_text === void 0 ? "" : _tile_main_text;
        var handleTitleTileClick = function() {
            var _layoutElement_on_click_event_name, _tile_event_tracking_token;
            onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick((_layoutElement_on_click_event_name = layoutElement.on_click_event_name) !== null && _layoutElement_on_click_event_name !== void 0 ? _layoutElement_on_click_event_name : "", requestId, (_tile_event_tracking_token = tile.event_tracking_token) !== null && _tile_event_tracking_token !== void 0 ? _tile_event_tracking_token : "");
        };
        return /*#__PURE__*/ _jsx(ImageTextLinkTile, {
            link: cta_link,
            image: image,
            images: images,
            text: main_text,
            imageSizeInfo: imageSizeInfo(),
            handleStyledLinkOnClick: handleTitleTileClick,
            useShowroomAnimatedTile: cta_link.includes(categoryShowroom()),
            dataTestId: "rectangular-title-tile"
        }, index);
    };
    var renderCategoryTile = function(tile, index) {
        var _tile_category_path_identifier;
        var isShowroom = (_tile_category_path_identifier = tile.category_path_identifier) === null || _tile_category_path_identifier === void 0 ? void 0 : _tile_category_path_identifier.includes(SHOWROOM_PATH_IDENTIFIER);
        if (!tile.image && !isShowroom || !tile.name) {
            return null;
        }
        var useAnimatedTile = !!layoutElement.background_color || layoutElement.display_page === ILayoutElement.DisplayPage.CATEGORY_PAGE;
        var handleOnClick = function() {
            var _layoutElement_on_click_event_name, _tile_category_path_identifier;
            onItemClick === null || onItemClick === void 0 ? void 0 : onItemClick((_layoutElement_on_click_event_name = layoutElement.on_click_event_name) !== null && _layoutElement_on_click_event_name !== void 0 ? _layoutElement_on_click_event_name : "", requestId, (_tile_category_path_identifier = tile.category_path_identifier) !== null && _tile_category_path_identifier !== void 0 ? _tile_category_path_identifier : "");
        };
        if (useAnimatedTile) {
            return /*#__PURE__*/ _jsx(StyledAnimatedCategoryTile, {
                categoryTile: tile,
                imageSizeInfo: imageSizeInfo(),
                $textColor: layoutElement.main_text_color,
                handleStyledLinkOnClick: handleOnClick
            }, index);
        } else {
            var _tile_cta_link;
            var path = (_tile_cta_link = tile.cta_link) !== null && _tile_cta_link !== void 0 ? _tile_cta_link : "";
            var _tile_images;
            return /*#__PURE__*/ _jsxs(CategoryTile, {
                linkTo: path,
                onClick: handleOnClick,
                children: [
                    isShowroom ? /*#__PURE__*/ _jsx(ShowroomAnimatedCategoryTile, {
                        images: (_tile_images = tile.images) !== null && _tile_images !== void 0 ? _tile_images : []
                    }) : /*#__PURE__*/ _jsxs(AspectRatioPicture, {
                        $width: "100%",
                        $ratio: VERTICAL_ASPECT_RATIO,
                        children: [
                            /*#__PURE__*/ _jsx(TransparencyOverlay, {}),
                            /*#__PURE__*/ _jsx("img", {
                                src: tile.image ? optimizeProductImageUrlFillVertical(tile.image, isSSR, 300) : EAEAEA_PIXEL,
                                alt: tile.name,
                                style: {
                                    width: "100%",
                                    height: "100%"
                                },
                                width: "100%",
                                height: "100%"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "1x"
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        color: layoutElement.main_text_color,
                        children: tile.name
                    })
                ]
            }, index);
        }
    };
    var renderItem = function(tile, index) {
        // ISimpleImageTitle tile is only used with this carousel on logged in category pages
        if (isSimpleImageTile(tile)) {
            return renderSimpleImageTitleTile(tile, index);
        }
        return renderCategoryTile(tile, index);
    };
    var onItemsVisible = function(items) {
        var itemTokens = items.map(function(item) {
            return isCategoryTile(item) ? item.category_path_identifier : item.event_tracking_token;
        });
        if (layoutElement.on_click_event_name) {
            var _itemTokens_join;
            onVisible === null || onVisible === void 0 ? void 0 : onVisible(layoutElement.on_click_event_name, requestId, (_itemTokens_join = itemTokens === null || itemTokens === void 0 ? void 0 : itemTokens.join(" ")) !== null && _itemTokens_join !== void 0 ? _itemTokens_join : "");
        }
    };
    var getItemsPerSlide = function() {
        if (layoutElement.display_page === ILayoutElement.DisplayPage.CATEGORY_PAGE || layoutElement.display_page === ILayoutElement.DisplayPage.SHOWROOM_PAGE) {
            return actualItemsPerSlideCategoryPage;
        }
        var _props_itemsPerSlide;
        return (_props_itemsPerSlide = props.itemsPerSlide) !== null && _props_itemsPerSlide !== void 0 ? _props_itemsPerSlide : ITEMS_PER_SLIDE_HOMEPAGE;
    };
    var getPeekNextPercent = function() {
        // don't try to peek the next tile if there is only enough content for one slide
        if ((layoutElement.display_page === ILayoutElement.DisplayPage.CATEGORY_PAGE || layoutElement.display_page === ILayoutElement.DisplayPage.SHOWROOM_PAGE) && getItems().length <= expectedItemsPerSlideCategoryPage) {
            return undefined;
        }
        return props.peekNextPercent;
    };
    return /*#__PURE__*/ _jsx(CarouselBaseComponent, _object_spread_props(_object_spread({
        buttonsOffsetTop: "calc(50% - 20px)",
        renderItem: renderItem,
        items: getItems(),
        onItemsVisible: onItemsVisible
    }, props), {
        itemsPerSlide: getItemsPerSlide(),
        peekNextPercent: getPeekNextPercent()
    }));
};
var CategoryTile = styled(EmbeddedLink).withConfig({
    componentId: "sc-b9592945-0"
})([
    "display:flex;flex-direction:column;text-decoration:none;",
    "{&:hover{text-decoration:none;}}"
], responsiveMediaQueries.canHover);
var StyledAnimatedCategoryTile = styled(AnimatedCategoryTile).withConfig({
    componentId: "sc-b9592945-1"
})([
    "",
    "{color:",
    ";}",
    "{background-image:",
    ";}"
], ImageTextLinkTileStyles.Text, function(param) {
    var $textColor = param.$textColor;
    return css([
        "",
        ""
    ], $textColor);
}, ImageTextLinkTileStyles.UnderlineText, function(param) {
    var $textColor = param.$textColor;
    return css([
        "linear-gradient(",
        ",",
        ")"
    ], $textColor, $textColor);
});
